import { Spine } from "@pixi/spine-pixi";
import * as PIXI from "pixi.js";
import { GameEngine } from "./server/GameEngine";
import { GlobalState } from "./shared/util/GlobalState";
import { SoundInstance } from "./shared/UI/SoundManagerUtils";
import { soundManager } from "./shared/UI/SoundManager";
import { gameStateSoundManager } from "./GameStateSoundManager";
import { ParticleSystem } from "./Particle";
import { GameAssets } from "./shared/util/GameAssets";

const WIN_FONT_SIZE = 100;
const BIG_WIN_FONT_SIZE = 150;
const WIN_THRESHOLD = [20, 40, 80];

const WIN_DURATIONS = {
    big: 11000,
    super: 15000,
    mega: 18000
}

export class WinCounter extends PIXI.Container {
    private winLabel: PIXI.BitmapText;
    private prize: number = 0;
    private odd: number = 0;
    private winTitles!: Spine;
    private winLayer: PIXI.Graphics;
    private isStopped: boolean = false;
    private level: number = -1;

    private alreadyPlayedWinSounds: Set<string> = new Set();

    private countUp: SoundInstance | undefined = undefined;
    private counterWin: SoundInstance | undefined = undefined;
    private bigWin: SoundInstance | undefined = undefined;
    private superWin: SoundInstance | undefined = undefined;
    private megaWin: SoundInstance | undefined = undefined;
    private particleSystem: ParticleSystem;

    constructor() {
        super();
        this.particleSystem = new ParticleSystem(
            GameAssets.textures.coinAtlas.animations.coin,
            100,
        );
        this.winLabel = new PIXI.BitmapText({
            text: "123456.00",
            style: {
                fontSize: WIN_FONT_SIZE,
                fontFamily: "winFont"
            }
        });

        this.winLabel.position.set(0, 200);
        this.winLabel.anchor.set(0.5);

        this.winLayer = new PIXI.Graphics().rect(-2000, -2000,
            4000, 4000).fill(0);
        this.winLayer.alpha = 0.7;

        this.winTitles = Spine.from({
            skeleton: 'winsJson',
            atlas: 'winsAtlas',
        });

        // console.log(this.winTitles);

        this.winTitles.state.addListener({
            event: function(entry, event) { },
            complete: (entry) => {
                if (entry.animation) {
                    this.emit(entry.animation.name);
                }
                // Handle quick stop sounds
                if (this.isStopped && entry.animation!.name.includes("Hide")) {
                    console.log("COMPLETED ANIMATION WHILE IS STOPPED IS TRUE");
                    switch (this.level) {
                        case 2:
                            if (this.bigWin) {
                                soundManager.stopSoundInstance(this.bigWin);
                                this.bigWin = undefined;
                                console.log("stopped big win sound");
                            }
                            break;
                        case 3:
                            if (this.superWin) {
                                soundManager.stopSoundInstance(this.superWin);
                                this.superWin = undefined;
                            }
                            break;
                        case 4:
                            if (this.megaWin) {
                                soundManager.stopSoundInstance(this.megaWin);
                                this.megaWin = undefined;
                            }
                            break;
                    }
                }
            },
            start: async (entry) => {
                if (this.isStopped && entry.animation!.name.includes("Hide")) {
                    switch (this.level) {
                        case 2:
                            await soundManager.playSound("bigWinEnd");
                            break;
                        case 3:
                            await soundManager.playSound("superWinEnd");
                            break;
                        case 4:
                            await soundManager.playSound("superWinEnd");
                            break;
                    }
                }
            },
            end: function(entry) { },
            dispose: function(entry) { },
        })
        this.winTitles.position.set(0, -120);
        this.winTitles.scale.set(0.4);

        this.addChild(this.winLayer);
        this.addChild(this.particleSystem);
        this.addChild(this.winTitles);
        this.addChild(this.winLabel);
        this.visible = false;
    }

    private _stopCountUpSoundForLevel(level: number) {
        if (this.countUp && this.level === level) {
            soundManager.stopSoundInstance(this.countUp);
            this.countUp = undefined;
            console.log("stopped count up sound");
        }
    }

    private async _showStaticWin() {
        return new Promise<void>((resolve) => {
            this.winLabel.style.fontSize = WIN_FONT_SIZE;
            this.winLabel.text = GameEngine.getInstance().getFormatter().format(this.prize);

            soundManager.playSound("win");

            setTimeout(() => {
                resolve();
            }, 1000);
        })
    }

    private async _showCounterWin() {
        return new Promise<void>(async (resolve) => {
            this.winLabel.style.fontSize = BIG_WIN_FONT_SIZE;
            this.winLabel.text = GameEngine.getInstance().getFormatter().format(this.prize);
            this.counterWin = await soundManager.playSound("winProgressive");
            await this._showProgressWin(3000);

            setTimeout(() => {
                if (this.counterWin) {
                    soundManager.stopSoundInstance(this.counterWin);
                    this.counterWin = undefined;
                }

                resolve();
            }, 1000);
        })
    }

    private async _showBigWin() {
        return new Promise<void>(async (resolve) => {
            this.particleSystem.start(0, 50, 100);
            this.winTitles.visible = true;
            this.winTitles.state.setAnimation(0, `bigWinShow`, false);
            this.winTitles.state.addAnimation(0, `bigWinLoop`, true);
            this.winLabel.style.fontSize = BIG_WIN_FONT_SIZE;
            await this._showProgressWin(WIN_DURATIONS.big);

            this.particleSystem.stop();
            setTimeout(() => {
                this.winTitles.state.setAnimation(0, `bigWinHide`, false);
                this.once('bigWinHide', resolve);
            }, 1000);
        })
    }

    private async _showSuperWin() {
        return new Promise<void>(async (resolve) => {
            this.particleSystem.start(0, 50, 70);
            this.winTitles.visible = true;
            this.winTitles.state.setAnimation(0, `bigWinShow`, false);
            this.winTitles.state.addAnimation(0, `bigWinLoop`, false);
            this.winTitles.state.addAnimation(0, `bigWinHide`, false);
            this.winTitles.state.addAnimation(0, `superWinShow`, false);
            this.winTitles.state.addAnimation(0, `superWinLoop`, true);
            this.winLabel.style.fontSize = BIG_WIN_FONT_SIZE;
            await this._showProgressWin(WIN_DURATIONS.super);

            this.particleSystem.stop();
            setTimeout(() => {
                this.winTitles.state.setAnimation(0, `superWinHide`, false);
                this.once('superWinHide', resolve);
            }, 1000);
        })
    }

    private async _showMegaWin() {
        return new Promise<void>(async (resolve) => {
            this.particleSystem.start(0, 50, 50);
            this.winTitles.visible = true;
            this.winTitles.state.setAnimation(0, `bigWinShow`, false); this.winTitles.state.addAnimation(0, `bigWinLoop`, false);
            this.winTitles.state.addAnimation(0, `bigWinHide`, false);
            this.winTitles.state.addAnimation(0, `superWinShow`, false);
            this.winTitles.state.addAnimation(0, `superWinLoop`, false);
            this.winTitles.state.addAnimation(0, `megaWinShow`, false);
            this.winTitles.state.addAnimation(0, `megaWinLoop`, true);
            this.winLabel.style.fontSize = BIG_WIN_FONT_SIZE;
            await this._showProgressWin(WIN_DURATIONS.mega);

            this.particleSystem.stop();
            setTimeout(() => {
                this.winTitles.state.setAnimation(0, `megaWinHide`, false);
                this.once('megaWinHide', resolve);
            }, 1000);
        })
    }

    private async _showProgressWin(duration: number = 0) {
        return new Promise<void>((resolve) => {
            const startTime = performance.now();
            const startValue = 0;
            const endValue = this.prize;

            const ticker = PIXI.Ticker.shared;

            this.isStopped = false; // Reset the stop flag
            this.winLabel.text = GameEngine.getInstance().getFormatter().format(0);

            const update = () => {
                if (this.isStopped) {
                    // If stopped, show the final value and resolve
                    this.winLabel.text = GameEngine.getInstance().getFormatter().format(endValue);
                    ticker.remove(update); // Stop the ticker update
                    resolve(); // Finish the promise
                    return;
                }

                const elapsed = performance.now() - startTime;
                const progress = Math.min(elapsed / duration, 1); // Ensure progress does not exceed 1
                const currentValue = startValue + (endValue - startValue) * progress;
                this.winLabel.text = GameEngine.getInstance().getFormatter().format(currentValue);

                if (progress === 1) {
                    this.winLabel.text = GameEngine.getInstance().getFormatter().format(currentValue);
                    ticker.remove(update); // Stop the ticker update
                    this._stopCountUpSoundForLevel(this.level);
                    setTimeout(resolve, 1000); // Resolve after delay
                }
            };

            ticker.add(update);
        });
    }

    public async showPrize(prize: number) {
        return new Promise<void>(async (resolve) => {
            this.isStopped = false;
            this.visible = true;
            this.winTitles.visible = false;

            gameStateSoundManager.updateBgMusicVolume(0.2);

            this.alreadyPlayedWinSounds.clear();

            this.prize = prize;
            this.odd = this.prize / GlobalState.getInstance().bet;
            if (this.odd < 2) {
                this.winLabel.position.set(0, 0);
                this.level = 0;
                await this._showStaticWin()
            } else if (this.odd < WIN_THRESHOLD[0]) {
                this.level = 1;
                this.winLabel.position.set(0, 0);

                await this._showCounterWin();
            } else if (this.odd < WIN_THRESHOLD[1]) {
                this.level = 2;

                this.bigWin = await soundManager.playSound("bigWin");
                this.winLabel.position.set(0, 200);

                await this._showBigWin();
            } else if (this.odd < WIN_THRESHOLD[2]) {
                this.level = 3;

                if (!this.alreadyPlayedWinSounds.has("superWin")) {
                    this.superWin = await soundManager.playSound("superWin");
                    this.alreadyPlayedWinSounds.add("superWin");
                }

                this.winLabel.position.set(0, 200);

                await this._showSuperWin();
            } else {
                this.level = 4;

                if (!this.alreadyPlayedWinSounds.has("megaWin")) {
                    this.megaWin = await soundManager.playSound("megaWin");
                    this.alreadyPlayedWinSounds.add("megaWin");
                }

                this.winLabel.position.set(0, 200);

                gameStateSoundManager.updateBgMusicVolume();

                await this._showMegaWin();
            }

            if (this.countUp) {
                soundManager.stopSoundInstance(this.countUp);
                this.countUp = undefined;
            }

            gameStateSoundManager.updateBgMusicVolume();

            this.isStopped = false;
            this.winTitles.visible = false;
            this.visible = false;
            resolve();
        })
    }

    public quickStop() {
        this.isStopped = true;
        switch (this.level) {
            case 0:
                break;
            case 1:
                break;
            case 2:
                break;
            case 3:
                if (this.winTitles.state.getCurrent(0)?.animation?.name.includes("big")) {
                    this.winTitles.state.setAnimation(0, `superWinShow`, false);
                    this.winTitles.state.addAnimation(0, `superWinLoop`, true);
                }
                break;
            case 4:
                if (this.winTitles.state.getCurrent(0)?.animation?.name.includes("big") && this.winTitles.state.getCurrent(0)?.animation?.name.includes("super")) {
                    this.winTitles.state.setAnimation(0, `megaWinShow`, false);
                    this.winTitles.state.addAnimation(0, `megaWinLoop`, true);
                }
                break;
        }
    }
}
